import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SendIcon, MessageCircleIcon, ChevronDownIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { saveSubmission } from './AdminDashboard';
import type { NetworkSubmission } from './AdminDashboard';
interface JoinNetworkFormProps {
  isOpen: boolean;
  onClose: () => void;
}
// Reason keys used internally (language-independent)
type ReasonKey = 'keepInformed' | 'volunteer' | 'collegeStudent' | 'socialMedia' | 'nonprofit' | 'faithBased' | 'business' | 'accomplishments';
interface ReasonOption {
  key: ReasonKey;
  labelEn: string;
  labelEs: string;
}
const REASON_OPTIONS: ReasonOption[] = [{
  key: 'keepInformed',
  labelEn: 'Keep me informed',
  labelEs: 'Manténganme informado'
}, {
  key: 'volunteer',
  labelEn: 'Interested in volunteering with the organization',
  labelEs: 'Interesado en ser voluntario con la organización'
}, {
  key: 'collegeStudent',
  labelEn: 'I am a college student looking to connect with fellow Honduran-American peers',
  labelEs: 'Soy estudiante universitario y busco conectar con otros jóvenes hondureño-americanos'
}, {
  key: 'socialMedia',
  labelEn: 'Share my social media platforms to highlight',
  labelEs: 'Compartir mis plataformas de redes sociales para destacar'
}, {
  key: 'nonprofit',
  labelEn: 'Share your nonprofit supporting Honduran-Americans/Honduras',
  labelEs: 'Comparte tu organización sin fines de lucro que apoya a hondureño-americanos/Honduras'
}, {
  key: 'faithBased',
  labelEn: 'Share the faith-based organization you lead',
  labelEs: 'Comparte la organización religiosa que lideras'
}, {
  key: 'business',
  labelEn: 'Share your Honduran-American business',
  labelEs: 'Comparte tu negocio hondureño-americano'
}, {
  key: 'accomplishments',
  labelEn: 'Share your accomplishments & story',
  labelEs: 'Comparte tus logros e historia'
}];
const US_STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC', 'PR', 'Other'];
const WHATSAPP_NUMBER = '12029001234';
const formatPhoneNumber = (value: string): string => {
  // Strip all non-digits
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};
const inputClass = 'w-full px-4 py-3.5 rounded-xl border-2 border-gray-200 font-nunito text-sm text-haa-navy focus:border-haa-blue focus:ring-0 outline-none transition-colors';
const subInputClass = 'w-full px-4 py-3 rounded-lg border border-gray-200 font-nunito text-sm text-haa-navy focus:border-haa-blue focus:ring-0 outline-none transition-colors bg-white';
export function JoinNetworkForm({
  isOpen,
  onClose
}: JoinNetworkFormProps) {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [selectedReasons, setSelectedReasons] = useState<ReasonKey[]>([]);
  const [identifiesHonduran, setIdentifiesHonduran] = useState<'yes' | 'no' | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  // Sub-fields for social media
  const [socialInstagram, setSocialInstagram] = useState('');
  const [socialFacebook, setSocialFacebook] = useState('');
  const [socialTiktok, setSocialTiktok] = useState('');
  // Sub-fields for nonprofit
  const [nonprofitName, setNonprofitName] = useState('');
  const [nonprofitDirector, setNonprofitDirector] = useState('');
  const [nonprofitWebsite, setNonprofitWebsite] = useState('');
  // Sub-fields for faith-based organization
  const [faithCity, setFaithCity] = useState('');
  const [faithState, setFaithState] = useState('');
  const [faithOrgName, setFaithOrgName] = useState('');
  const [faithWebsite, setFaithWebsite] = useState('');
  // Sub-fields for business
  const [businessName, setBusinessName] = useState('');
  const [businessContact, setBusinessContact] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [businessWebsite, setBusinessWebsite] = useState('');
  // Sub-fields for accomplishments
  const [accomplishmentWebsite, setAccomplishmentWebsite] = useState('');
  const [accomplishmentSocial, setAccomplishmentSocial] = useState('');
  const [accomplishmentLinkedin, setAccomplishmentLinkedin] = useState('');
  const l = isEs ? {
    title: 'Únete a Nuestra Red',
    subtitle: 'Completa el formulario y conecta con la Asociación Hondureño-Americana.',
    firstName: 'Nombre',
    lastName: 'Apellido o Inicial',
    email: 'Correo Electrónico',
    phone: 'Teléfono (opcional)',
    city: 'Ciudad',
    state: 'Estado',
    selectState: 'Seleccionar estado',
    reasonLabel: 'Razón de Contacto',
    required: 'Los campos marcados con * son obligatorios',
    successTitle: '¡Enviado!',
    successMessage: 'Tu información ha sido enviada. Nos pondremos en contacto pronto.',
    close: 'Cerrar',
    or: 'o',
    // Social media sub-field labels
    instagram: 'Instagram',
    facebook: 'Facebook',
    tiktok: 'TikTok',
    // Sub-field labels
    nonprofitName: 'Nombre de la Organización',
    nonprofitDirector: 'Director(a)',
    nonprofitWebsite: 'Sitio Web',
    faithCity: 'Ciudad',
    faithState: 'Estado',
    faithOrgName: 'Nombre de la Organización',
    faithWebsite: 'Sitio Web o Red Social',
    businessName: 'Nombre del Negocio',
    businessContact: 'Persona de Contacto',
    businessPhone: 'Teléfono del Negocio',
    businessWebsite: 'Sitio Web o Red Social',
    accWebsite: 'Sitio Web',
    accSocial: 'Red Social',
    accLinkedin: 'LinkedIn'
  } : {
    title: 'Join Our Network',
    subtitle: 'Fill out the form below to connect with the Honduran-American Association.',
    firstName: 'First Name',
    lastName: 'Last Name or Initial',
    email: 'Email',
    phone: 'Phone (optional)',
    city: 'City',
    state: 'State',
    selectState: 'Select state',
    reasonLabel: 'Reason for Contacting',
    required: 'Fields marked with * are required',
    successTitle: 'Sent!',
    successMessage: "Your information has been submitted. We'll be in touch soon.",
    close: 'Close',
    or: 'or',
    // Social media sub-field labels
    instagram: 'Instagram',
    facebook: 'Facebook',
    tiktok: 'TikTok',
    // Sub-field labels
    nonprofitName: 'Nonprofit Name',
    nonprofitDirector: 'Director',
    nonprofitWebsite: 'Website',
    faithCity: 'City',
    faithState: 'State',
    faithOrgName: 'Organization Name',
    faithWebsite: 'Website or Social Media Link',
    businessName: 'Business Name',
    businessContact: 'Contact Person',
    businessPhone: 'Business Phone',
    businessWebsite: 'Website or Social Media Handle',
    accWebsite: 'Website',
    accSocial: 'Social Media',
    accLinkedin: 'LinkedIn'
  };
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const toggleReason = (key: ReasonKey) => {
    setSelectedReasons(prev => prev.includes(key) ? prev.filter(r => r !== key) : [...prev, key]);
  };
  const getReasonLabel = (opt: ReasonOption) => isEs ? opt.labelEs : opt.labelEn;
  const buildMessageBody = () => {
    const reasonLabels = selectedReasons.map(key => {
      const opt = REASON_OPTIONS.find(o => o.key === key);
      return opt ? getReasonLabel(opt) : key;
    });
    const reasonsText = reasonLabels.length > 0 ? reasonLabels.join('; ') : 'N/A';
    let body = `${l.firstName}: ${firstName}
${l.lastName}: ${lastName}
${l.email}: ${email}
${l.phone}: ${phone}
${l.city}: ${city}
${l.state}: ${state}
${l.reasonLabel}: ${reasonsText}`;
    if (selectedReasons.includes('socialMedia') && (socialInstagram || socialFacebook || socialTiktok)) {
      body += `\n\n--- ${isEs ? 'Redes Sociales' : 'Social Media Platforms'} ---`;
      if (socialInstagram) body += `\n${l.instagram}: ${socialInstagram}`;
      if (socialFacebook) body += `\n${l.facebook}: ${socialFacebook}`;
      if (socialTiktok) body += `\n${l.tiktok}: ${socialTiktok}`;
    }
    if (selectedReasons.includes('nonprofit') && (nonprofitName || nonprofitDirector || nonprofitWebsite)) {
      body += `\n\n--- ${isEs ? 'Información de la Organización' : 'Nonprofit Details'} ---`;
      if (nonprofitName) body += `\n${l.nonprofitName}: ${nonprofitName}`;
      if (nonprofitDirector) body += `\n${l.nonprofitDirector}: ${nonprofitDirector}`;
      if (nonprofitWebsite) body += `\n${l.nonprofitWebsite}: ${nonprofitWebsite}`;
    }
    if (selectedReasons.includes('faithBased') && (faithCity || faithState || faithOrgName || faithWebsite)) {
      body += `\n\n--- ${isEs ? 'Organización Religiosa' : 'Faith-Based Organization'} ---`;
      if (faithOrgName) body += `\n${l.faithOrgName}: ${faithOrgName}`;
      if (faithCity) body += `\n${l.faithCity}: ${faithCity}`;
      if (faithState) body += `\n${l.faithState}: ${faithState}`;
      if (faithWebsite) body += `\n${l.faithWebsite}: ${faithWebsite}`;
    }
    if (selectedReasons.includes('business') && (businessName || businessContact || businessPhone || businessWebsite)) {
      body += `\n\n--- ${isEs ? 'Información del Negocio' : 'Business Details'} ---`;
      if (businessName) body += `\n${l.businessName}: ${businessName}`;
      if (businessContact) body += `\n${l.businessContact}: ${businessContact}`;
      if (businessPhone) body += `\n${l.businessPhone}: ${businessPhone}`;
      if (businessWebsite) body += `\n${l.businessWebsite}: ${businessWebsite}`;
    }
    if (selectedReasons.includes('accomplishments') && (accomplishmentWebsite || accomplishmentSocial || accomplishmentLinkedin)) {
      body += `\n\n--- ${isEs ? 'Información de Logros' : 'Accomplishments Details'} ---`;
      if (accomplishmentWebsite) body += `\n${l.accWebsite}: ${accomplishmentWebsite}`;
      if (accomplishmentSocial) body += `\n${l.accSocial}: ${accomplishmentSocial}`;
      if (accomplishmentLinkedin) body += `\n${l.accLinkedin}: ${accomplishmentLinkedin}`;
    }
    return body;
  };
  const [submitting, setSubmitting] = useState(false);
  const SHEETS_URL = 'https://script.google.com/macros/s/AKfycbyZzuAyC07JGli05_9stgT14MrywttY1WzN1zQTiNnhIfPMxE61PaFIwzqZ2N9mcQSs5Q/exec';
  const sendToGoogleSheets = (data: Record<string, any>) => {
    // Using text/plain content type avoids CORS preflight entirely
    // The JSON string arrives in e.postData.contents on the Apps Script side
    fetch(SHEETS_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'text/plain;charset=utf-8'
      },
      body: JSON.stringify(data)
    }).catch(err => {
      console.warn('Google Sheets submission error:', err);
    });
  };
  const handleSubmit = () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    const submission: NetworkSubmission = {
      id: Date.now().toString() + Math.random().toString(36).slice(2, 8),
      timestamp: new Date().toISOString(),
      firstName,
      lastName,
      email,
      phone,
      city,
      state,
      reasons: selectedReasons,
      socialInstagram: selectedReasons.includes('socialMedia') ? socialInstagram : undefined,
      socialFacebook: selectedReasons.includes('socialMedia') ? socialFacebook : undefined,
      socialTiktok: selectedReasons.includes('socialMedia') ? socialTiktok : undefined,
      nonprofitName: selectedReasons.includes('nonprofit') ? nonprofitName : undefined,
      nonprofitDirector: selectedReasons.includes('nonprofit') ? nonprofitDirector : undefined,
      nonprofitWebsite: selectedReasons.includes('nonprofit') ? nonprofitWebsite : undefined,
      faithCity: selectedReasons.includes('faithBased') ? faithCity : undefined,
      faithState: selectedReasons.includes('faithBased') ? faithState : undefined,
      faithOrgName: selectedReasons.includes('faithBased') ? faithOrgName : undefined,
      faithWebsite: selectedReasons.includes('faithBased') ? faithWebsite : undefined,
      businessName: selectedReasons.includes('business') ? businessName : undefined,
      businessContact: selectedReasons.includes('business') ? businessContact : undefined,
      businessPhone: selectedReasons.includes('business') ? businessPhone : undefined,
      businessWebsite: selectedReasons.includes('business') ? businessWebsite : undefined,
      accomplishmentWebsite: selectedReasons.includes('accomplishments') ? accomplishmentWebsite : undefined,
      accomplishmentSocial: selectedReasons.includes('accomplishments') ? accomplishmentSocial : undefined,
      accomplishmentLinkedin: selectedReasons.includes('accomplishments') ? accomplishmentLinkedin : undefined,
      identifiesHonduran: identifiesHonduran ?? undefined
    };
    // Save locally as backup
    saveSubmission(submission);
    // Send to Google Sheets
    sendToGoogleSheets({
      timestamp: new Date().toISOString(),
      firstName,
      lastName,
      email,
      phone,
      city,
      state,
      identifiesHonduran: identifiesHonduran ?? '',
      reasons: selectedReasons.join(', '),
      socialInstagram: selectedReasons.includes('socialMedia') ? socialInstagram : '',
      socialFacebook: selectedReasons.includes('socialMedia') ? socialFacebook : '',
      socialTiktok: selectedReasons.includes('socialMedia') ? socialTiktok : '',
      nonprofitName: selectedReasons.includes('nonprofit') ? nonprofitName : '',
      nonprofitDirector: selectedReasons.includes('nonprofit') ? nonprofitDirector : '',
      nonprofitWebsite: selectedReasons.includes('nonprofit') ? nonprofitWebsite : '',
      faithCity: selectedReasons.includes('faithBased') ? faithCity : '',
      faithState: selectedReasons.includes('faithBased') ? faithState : '',
      faithOrgName: selectedReasons.includes('faithBased') ? faithOrgName : '',
      faithWebsite: selectedReasons.includes('faithBased') ? faithWebsite : '',
      businessName: selectedReasons.includes('business') ? businessName : '',
      businessContact: selectedReasons.includes('business') ? businessContact : '',
      businessPhone: selectedReasons.includes('business') ? businessPhone : '',
      businessWebsite: selectedReasons.includes('business') ? businessWebsite : '',
      accomplishmentWebsite: selectedReasons.includes('accomplishments') ? accomplishmentWebsite : '',
      accomplishmentSocial: selectedReasons.includes('accomplishments') ? accomplishmentSocial : '',
      accomplishmentLinkedin: selectedReasons.includes('accomplishments') ? accomplishmentLinkedin : ''
    });
    setSubmitting(false);
    setSubmitted(true);
  };
  const handleSendEmail = () => {
    const subject = encodeURIComponent(isEs ? 'Solicitud para Unirse a la Red - HAA' : 'Join Our Network Request - HAA');
    const body = encodeURIComponent(buildMessageBody());
    window.open(`mailto:info@honduranamerican.org?subject=${subject}&body=${body}`, '_blank');
    setSubmitted(true);
  };
  const handleSendWhatsApp = () => {
    const text = encodeURIComponent(`*${isEs ? 'Solicitud para Unirse a la Red - HAA' : 'Join Our Network - HAA'}*\n\n${buildMessageBody()}`);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, '_blank');
    setSubmitted(true);
  };
  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setCity('');
      setState('');
      setSelectedReasons([]);
      setSubmitted(false);
      setSocialInstagram('');
      setSocialFacebook('');
      setSocialTiktok('');
      setNonprofitName('');
      setNonprofitDirector('');
      setNonprofitWebsite('');
      setFaithCity('');
      setFaithState('');
      setFaithOrgName('');
      setFaithWebsite('');
      setBusinessName('');
      setBusinessContact('');
      setBusinessPhone('');
      setBusinessWebsite('');
      setAccomplishmentWebsite('');
      setAccomplishmentSocial('');
      setAccomplishmentLinkedin('');
      setIdentifiesHonduran(null);
    }, 300);
  };
  const isValid = firstName.trim() && lastName.trim();
  return <AnimatePresence data-id="element-2478">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-4 pt-20 sm:pt-4" onClick={handleClose} data-id="element-2479">
          <div className="absolute inset-0 bg-haa-navy/60 backdrop-blur-sm" data-id="element-2480" />

          <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 30,
        scale: 0.97
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} onClick={e => e.stopPropagation()} className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto z-10" data-id="element-2481">
            {/* Header */}
            <div className="sticky top-0 bg-white rounded-t-3xl border-b border-gray-100 px-6 sm:px-8 py-5 flex items-start justify-between z-10" data-id="element-2482">
              <div data-id="element-2483">
                <h3 className="font-bebas text-3xl sm:text-4xl text-haa-navy tracking-wider leading-tight" data-id="element-2484">
                  {l.title}
                </h3>
                <p className="font-nunito text-gray-500 text-sm mt-1" data-id="element-2485">
                  {l.subtitle}
                </p>
              </div>
              <button onClick={handleClose} className="flex-shrink-0 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors ml-4 mt-1" aria-label={l.close} data-id="element-2486">
                <XIcon className="w-4 h-4 text-gray-500" data-id="element-2487" />
              </button>
            </div>

            <AnimatePresence mode="wait" data-id="element-2488">
              {!submitted ? <motion.div key="form" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="px-6 sm:px-8 py-6 space-y-4" data-id="element-2489">
                  {/* Name Row */}
                  <div className="grid grid-cols-2 gap-3" data-id="element-2490">
                    <div data-id="element-2491">
                      <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block" data-id="element-2492">
                        {l.firstName} *
                      </label>
                      <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className={inputClass} placeholder={l.firstName} data-id="element-2493" />
                    </div>
                    <div data-id="element-2494">
                      <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block" data-id="element-2495">
                        {l.lastName} *
                      </label>
                      <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className={inputClass} placeholder={l.lastName} data-id="element-2496" />
                    </div>
                  </div>

                  {/* Email */}
                  <div data-id="element-2497">
                    <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block" data-id="element-2498">
                      {l.email}
                    </label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputClass} placeholder="you@example.com" data-id="element-2499" />
                  </div>

                  {/* Phone */}
                  <div data-id="element-2500">
                    <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block" data-id="element-2501">
                      {l.phone}
                    </label>
                    <input type="tel" value={phone} onChange={e => setPhone(formatPhoneNumber(e.target.value))} className={inputClass} placeholder="555-123-4567" data-id="element-2502" />
                  </div>

                  {/* City + State */}
                  <div className="grid grid-cols-2 gap-3" data-id="element-2503">
                    <div data-id="element-2504">
                      <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block" data-id="element-2505">
                        {l.city}
                      </label>
                      <input type="text" value={city} onChange={e => setCity(e.target.value)} className={inputClass} placeholder={l.city} data-id="element-2506" />
                    </div>
                    <div className="relative" data-id="element-2507">
                      <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block" data-id="element-2508">
                        {l.state}
                      </label>
                      <button type="button" onClick={() => setStateDropdownOpen(!stateDropdownOpen)} className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 font-nunito text-sm text-left flex items-center justify-between focus:border-haa-blue outline-none transition-colors" data-id="element-2509">
                        <span className={state ? 'text-haa-navy' : 'text-gray-400'} data-id="element-2510">
                          {state || l.selectState}
                        </span>
                        <ChevronDownIcon className="w-4 h-4 text-gray-400" data-id="element-2511" />
                      </button>
                      <AnimatePresence data-id="element-2512">
                        {stateDropdownOpen && <motion.div initial={{
                    opacity: 0,
                    y: -4
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -4
                  }} transition={{
                    duration: 0.15
                  }} className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-20 max-h-48 overflow-y-auto" data-id="element-2513">
                            {US_STATES.map(s => <button key={s} type="button" onClick={() => {
                      setState(s);
                      setStateDropdownOpen(false);
                    }} className={`w-full text-left px-4 py-2 font-nunito text-sm hover:bg-blue-50 transition-colors ${state === s ? 'bg-blue-50 text-haa-blue font-bold' : 'text-gray-700'}`} data-id="element-2514">
                                {s}
                              </button>)}
                          </motion.div>}
                      </AnimatePresence>
                    </div>
                  </div>

                  {/* Honduran Identity Question */}
                  <div data-id="element-2515">
                    <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block" data-id="element-2516">
                      {isEs ? '¿Te identificas como hondureño-americano/hondureño?' : 'Do you identify as Honduran-American/Honduran?'}
                    </label>
                    <div className="flex gap-3" data-id="element-2517">
                      <button type="button" onClick={() => setIdentifiesHonduran('yes')} className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 font-nunito text-sm font-semibold transition-all duration-200 ${identifiesHonduran === 'yes' ? 'border-haa-blue bg-blue-50 text-haa-navy' : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-2518">
                        {identifiesHonduran === 'yes' && <CheckIcon className="w-4 h-4 text-haa-blue" data-id="element-2519" />}
                        {isEs ? 'Sí' : 'Yes'}
                      </button>
                      <button type="button" onClick={() => setIdentifiesHonduran('no')} className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 font-nunito text-sm font-semibold transition-all duration-200 ${identifiesHonduran === 'no' ? 'border-haa-blue bg-blue-50 text-haa-navy' : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-2520">
                        {identifiesHonduran === 'no' && <CheckIcon className="w-4 h-4 text-haa-blue" data-id="element-2521" />}
                        No
                      </button>
                    </div>
                  </div>

                  {/* Reason for Contacting */}
                  <div data-id="element-2522">
                    <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block" data-id="element-2523">
                      {l.reasonLabel}
                    </label>
                    <p className="font-nunito text-[11px] text-gray-400 italic mb-2" data-id="element-2524">
                      {isEs ? 'Seleccione todas las que apliquen' : 'Check all that apply'}
                    </p>
                    <div className="space-y-2" data-id="element-2525">
                      {REASON_OPTIONS.map(opt => {
                  const isSelected = selectedReasons.includes(opt.key);
                  return <div key={opt.key} data-id="element-2526">
                            <button type="button" onClick={() => toggleReason(opt.key)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left font-nunito text-sm transition-all duration-200 ${isSelected ? 'border-haa-blue bg-blue-50 text-haa-navy' : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-2527">
                              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200 ${isSelected ? 'border-haa-blue bg-haa-blue' : 'border-gray-300'}`} data-id="element-2528">
                                {isSelected && <CheckIcon className="w-3 h-3 text-white" data-id="element-2529" />}
                              </div>
                              <span className="leading-snug" data-id="element-2530">
                                {getReasonLabel(opt)}
                              </span>
                            </button>

                            {/* Conditional sub-fields for Nonprofit */}
                            <AnimatePresence data-id="element-2531">
                              {opt.key === 'nonprofit' && isSelected && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.25,
                        ease: [0.16, 1, 0.3, 1]
                      }} className="overflow-hidden" data-id="element-2532">
                                  <div className="ml-8 mt-2 mb-1 space-y-2 p-3 bg-blue-50/50 rounded-lg border border-blue-100" data-id="element-2533">
                                    <div data-id="element-2534">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2535">
                                        {l.nonprofitName}
                                      </label>
                                      <input type="text" value={nonprofitName} onChange={e => setNonprofitName(e.target.value)} className={subInputClass} placeholder={l.nonprofitName} data-id="element-2536" />
                                    </div>
                                    <div data-id="element-2537">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2538">
                                        {l.nonprofitDirector}
                                      </label>
                                      <input type="text" value={nonprofitDirector} onChange={e => setNonprofitDirector(e.target.value)} className={subInputClass} placeholder={l.nonprofitDirector} data-id="element-2539" />
                                    </div>
                                    <div data-id="element-2540">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2541">
                                        {l.nonprofitWebsite}
                                      </label>
                                      <input type="url" value={nonprofitWebsite} onChange={e => setNonprofitWebsite(e.target.value)} className={subInputClass} placeholder="https://" data-id="element-2542" />
                                    </div>
                                  </div>
                                </motion.div>}
                            </AnimatePresence>

                            {/* Conditional sub-fields for Faith-Based Organization */}
                            <AnimatePresence data-id="element-2543">
                              {opt.key === 'faithBased' && isSelected && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.25,
                        ease: [0.16, 1, 0.3, 1]
                      }} className="overflow-hidden" data-id="element-2544">
                                  <div className="ml-8 mt-2 mb-1 space-y-2 p-3 bg-blue-50/50 rounded-lg border border-blue-100" data-id="element-2545">
                                    <div data-id="element-2546">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2547">
                                        {l.faithOrgName}
                                      </label>
                                      <input type="text" value={faithOrgName} onChange={e => setFaithOrgName(e.target.value)} className={subInputClass} placeholder={l.faithOrgName} data-id="element-2548" />
                                    </div>
                                    <div data-id="element-2549">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2550">
                                        {l.faithCity}
                                      </label>
                                      <input type="text" value={faithCity} onChange={e => setFaithCity(e.target.value)} className={subInputClass} placeholder={l.faithCity} data-id="element-2551" />
                                    </div>
                                    <div data-id="element-2552">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2553">
                                        {l.faithState}
                                      </label>
                                      <input type="text" value={faithState} onChange={e => setFaithState(e.target.value)} className={subInputClass} placeholder={l.faithState} data-id="element-2554" />
                                    </div>
                                    <div data-id="element-2555">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2556">
                                        {l.faithWebsite}
                                      </label>
                                      <input type="url" value={faithWebsite} onChange={e => setFaithWebsite(e.target.value)} className={subInputClass} placeholder="https://" data-id="element-2557" />
                                    </div>
                                  </div>
                                </motion.div>}
                            </AnimatePresence>

                            {/* Conditional sub-fields for Social Media */}
                            <AnimatePresence data-id="element-2558">
                              {opt.key === 'socialMedia' && isSelected && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.25,
                        ease: [0.16, 1, 0.3, 1]
                      }} className="overflow-hidden" data-id="element-2559">
                                  <div className="ml-8 mt-2 mb-1 space-y-2 p-3 bg-blue-50/50 rounded-lg border border-blue-100" data-id="element-2560">
                                    <div data-id="element-2561">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2562">
                                        {l.instagram}
                                      </label>
                                      <input type="text" value={socialInstagram} onChange={e => setSocialInstagram(e.target.value)} className={subInputClass} placeholder="@username" data-id="element-2563" />
                                    </div>
                                    <div data-id="element-2564">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2565">
                                        {l.facebook}
                                      </label>
                                      <input type="text" value={socialFacebook} onChange={e => setSocialFacebook(e.target.value)} className={subInputClass} placeholder="facebook.com/yourpage" data-id="element-2566" />
                                    </div>
                                    <div data-id="element-2567">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2568">
                                        {l.tiktok}
                                      </label>
                                      <input type="text" value={socialTiktok} onChange={e => setSocialTiktok(e.target.value)} className={subInputClass} placeholder="@username" data-id="element-2569" />
                                    </div>
                                  </div>
                                </motion.div>}
                            </AnimatePresence>

                            {/* Conditional sub-fields for Business */}
                            <AnimatePresence data-id="element-2570">
                              {opt.key === 'business' && isSelected && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.25,
                        ease: [0.16, 1, 0.3, 1]
                      }} className="overflow-hidden" data-id="element-2571">
                                  <div className="ml-8 mt-2 mb-1 space-y-2 p-3 bg-blue-50/50 rounded-lg border border-blue-100" data-id="element-2572">
                                    <div data-id="element-2573">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2574">
                                        {l.businessName}
                                      </label>
                                      <input type="text" value={businessName} onChange={e => setBusinessName(e.target.value)} className={subInputClass} placeholder={l.businessName} data-id="element-2575" />
                                    </div>
                                    <div data-id="element-2576">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2577">
                                        {l.businessContact}
                                      </label>
                                      <input type="text" value={businessContact} onChange={e => setBusinessContact(e.target.value)} className={subInputClass} placeholder={l.businessContact} data-id="element-2578" />
                                    </div>
                                    <div data-id="element-2579">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2580">
                                        {l.businessPhone}
                                      </label>
                                      <input type="tel" value={businessPhone} onChange={e => setBusinessPhone(formatPhoneNumber(e.target.value))} className={subInputClass} placeholder="555-123-4567" data-id="element-2581" />
                                    </div>
                                    <div data-id="element-2582">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2583">
                                        {l.businessWebsite}
                                      </label>
                                      <input type="text" value={businessWebsite} onChange={e => setBusinessWebsite(e.target.value)} className={subInputClass} placeholder="https:// or @handle" data-id="element-2584" />
                                    </div>
                                  </div>
                                </motion.div>}
                            </AnimatePresence>

                            {/* Conditional sub-fields for Accomplishments */}
                            <AnimatePresence data-id="element-2585">
                              {opt.key === 'accomplishments' && isSelected && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.25,
                        ease: [0.16, 1, 0.3, 1]
                      }} className="overflow-hidden" data-id="element-2586">
                                  <div className="ml-8 mt-2 mb-1 space-y-2 p-3 bg-blue-50/50 rounded-lg border border-blue-100" data-id="element-2587">
                                    <div data-id="element-2588">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2589">
                                        {l.accWebsite}
                                      </label>
                                      <input type="url" value={accomplishmentWebsite} onChange={e => setAccomplishmentWebsite(e.target.value)} className={subInputClass} placeholder="https://" data-id="element-2590" />
                                    </div>
                                    <div data-id="element-2591">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2592">
                                        {l.accSocial}
                                      </label>
                                      <input type="text" value={accomplishmentSocial} onChange={e => setAccomplishmentSocial(e.target.value)} className={subInputClass} placeholder="@handle" data-id="element-2593" />
                                    </div>
                                    <div data-id="element-2594">
                                      <label className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-2595">
                                        {l.accLinkedin}
                                      </label>
                                      <input type="url" value={accomplishmentLinkedin} onChange={e => setAccomplishmentLinkedin(e.target.value)} className={subInputClass} placeholder="https://linkedin.com/in/..." data-id="element-2596" />
                                    </div>
                                  </div>
                                </motion.div>}
                            </AnimatePresence>
                          </div>;
                })}
                    </div>
                  </div>

                  {/* Required note */}
                  <p className="font-nunito text-xs text-gray-400 pt-1" data-id="element-2597">
                    {l.required}
                  </p>

                  {/* Submit Buttons */}
                  <div className="flex flex-col gap-3 pt-2 pb-2" data-id="element-2598">
                    <motion.button whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={handleSubmit} disabled={!isValid} className={`w-full flex items-center justify-center gap-2 font-nunito font-bold text-base px-6 py-4 rounded-full shadow-lg transition-all ${isValid ? 'bg-gradient-to-r from-haa-blue to-haa-midblue text-white hover:shadow-xl' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-2599">
                      <SendIcon className="w-4 h-4" data-id="element-2600" />
                      {isEs ? 'Enviar' : 'Submit'}
                    </motion.button>
                  </div>
                </motion.div> : <motion.div key="success" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="px-6 sm:px-8 py-16 flex flex-col items-center text-center" data-id="element-2601">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.1,
              type: 'spring',
              stiffness: 200
            }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-5" data-id="element-2602">
                    <CheckIcon className="w-8 h-8 text-green-600" data-id="element-2603" />
                  </motion.div>
                  <h3 className="font-bebas text-4xl text-haa-navy tracking-wider mb-2" data-id="element-2604">
                    {l.successTitle}
                  </h3>
                  <p className="font-nunito text-gray-600 text-base mb-8 max-w-sm" data-id="element-2605">
                    {l.successMessage}
                  </p>
                  <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} onClick={handleClose} className="inline-flex items-center gap-2 bg-haa-navy text-white font-nunito font-bold text-base px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all" data-id="element-2606">
                    {l.close}
                  </motion.button>
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}